import { useEffect, useState } from "react";
import axios from "axios";
import config from "./config";
import PoweredByFlexiFunnels from "./PoweredByFlexiFunnels";
import GroupSwitcher from "./GroupSwitcher";
import { rgba, shade } from "./course-templates/shared";

// Compact member-count label: 80500 -> "80.5k", 3300 -> "3.3k".
function fmtCount(n) {
  const v = Number(n) || 0;
  if (v >= 1000) {
    const k = v / 1000;
    return `${k % 1 === 0 ? k : k.toFixed(1)}k`;
  }
  return String(v);
}

/**
 * Instructor details page — opened from the "By <instructor>" link on a group
 * landing page. Shown via in-page state on the same /:token route (no URL
 * param). Mirrors the Skool instructor profile: a profile card plus the
 * "Owned by <name>" list of every group linked to this instructor. (Activity
 * heatmap / follower stats / social links are intentionally omitted — no
 * backing data.)
 */
// Access type: 1 = Public, 2 = Private, 3 = Paid.
const accessTypeLabel = (t) =>
  Number(t) === 2 ? "Private" : Number(t) === 3 ? "Paid" : "Public";

// Social platform icons (rendered for each link stored on the instructor).
// Order controls how they appear in the row.
const SOCIAL_ORDER = ["website", "instagram", "twitter", "youtube", "linkedin", "facebook", "whatsapp"];
const SOCIAL_ICONS = {
  website: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 10.5a4 4 0 010 5.66l-2.5 2.5a4 4 0 01-5.66-5.66l1.5-1.5M10.5 13.5a4 4 0 010-5.66l2.5-2.5a4 4 0 015.66 5.66l-1.5 1.5" />
    </svg>
  ),
  instagram: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M12 2.16c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.43.36 1.06.41 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.43.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.16-.43-.36-1.06-.41-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.05-1.17.25-1.8.41-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.43-.16 1.06-.36 2.23-.41C8.42 2.17 8.8 2.16 12 2.16zM12 0C8.74 0 8.33.01 7.05.07 5.78.13 4.9.33 4.14.63a5.9 5.9 0 00-2.13 1.38A5.9 5.9 0 00.63 4.14C.33 4.9.13 5.78.07 7.05.01 8.33 0 8.74 0 12s.01 3.67.07 4.95c.06 1.27.26 2.15.56 2.91.31.79.72 1.46 1.38 2.13.67.66 1.34 1.07 2.13 1.38.76.3 1.64.5 2.91.56C8.33 23.99 8.74 24 12 24s3.67-.01 4.95-.07c1.27-.06 2.15-.26 2.91-.56a5.9 5.9 0 002.13-1.38 5.9 5.9 0 001.38-2.13c.3-.76.5-1.64.56-2.91.06-1.28.07-1.69.07-4.95s-.01-3.67-.07-4.95c-.06-1.27-.26-2.15-.56-2.91a5.9 5.9 0 00-1.38-2.13A5.9 5.9 0 0019.86.63c-.76-.3-1.64-.5-2.91-.56C15.67.01 15.26 0 12 0zm0 5.84A6.16 6.16 0 1018.16 12 6.16 6.16 0 0012 5.84zm0 10.16A4 4 0 1116 12a4 4 0 01-4 4zm6.41-11.85a1.44 1.44 0 11-1.44-1.44 1.44 1.44 0 011.44 1.44z" />
    </svg>
  ),
  twitter: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M18.9 1.15h3.68l-8.04 9.19L24 22.85h-7.4l-5.8-7.58-6.63 7.58H.49l8.6-9.83L0 1.15h7.59l5.24 6.93 6.07-6.93zm-1.29 19.5h2.04L6.49 3.24H4.3l13.31 17.41z" />
    </svg>
  ),
  youtube: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M23.5 6.2a3 3 0 00-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 00.5 6.2 31 31 0 000 12a31 31 0 00.5 5.8 3 3 0 002.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 002.1-2.1A31 31 0 0024 12a31 31 0 00-.5-5.8zM9.6 15.6V8.4l6.2 3.6-6.2 3.6z" />
    </svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M19 0h-14C2.24 0 0 2.24 0 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5V5c0-2.76-2.24-5-5-5zM7.12 20.45H3.56V9h3.56v11.45zM5.34 7.43a2.06 2.06 0 110-4.12 2.06 2.06 0 010 4.12zM20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.85 0-2.14 1.45-2.14 2.94v5.67H9.35V9h3.41v1.56h.05c.47-.9 1.63-1.85 3.37-1.85 3.6 0 4.27 2.37 4.27 5.46v6.28z" />
    </svg>
  ),
  facebook: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.5 1.49-3.89 3.78-3.89 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z" />
    </svg>
  ),
  whatsapp: (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  ),
};

export default function InstructorDetails({ token, instructorId, accent, dark = false, member, onView, onLogin, onBack, myGroups = [], onSwitchGroup, onDiscoverGroups }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let on = true;
    setLoading(true);
    setError("");
    axios
      .post(`${config.COMMUNITY_API_URL}/community-instructor`, {
        community_token: token,
        instructor_id: instructorId,
      })
      .then((res) => {
        if (!on) return;
        if (res.data?.error === 0) setData(res.data);
        else setError(res.data?.msg || "Group Owner not found.");
      })
      .catch(() => on && setError("Something went wrong."))
      .finally(() => on && setLoading(false));
    return () => {
      on = false;
    };
  }, [token, instructorId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-400" style={{ background: dark ? "#0e1220" : "#f8fafc" }}>
        Loading…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 text-gray-500" style={{ background: dark ? "#0e1220" : "#f8fafc" }}>
        <p>{error || "Not found"}</p>
      </div>
    );
  }

  const { community, instructor, groups } = data;
  // Match the template the instructor link was clicked from; fall back to the
  // community primary colour.
  const primary = (accent && String(accent).trim()) || community.primary_color || "#0274F7";
  const firstName = (instructor.name || "").trim().split(/\s+/)[0] || instructor.name;
  const initial = instructor.name ? instructor.name.charAt(0).toUpperCase() : "?";

  // Always a LIGHT page. The template accent is applied as a soft LIGHT gradient
  // (the dark templates looked too heavy here). `dark` is intentionally unused.
  const PAGE = dark ? "#0e1220" : "#f8fafc";
  const CARD = dark ? "#161d2e" : "#ffffff";
  const BD = dark ? "rgba(255,255,255,.09)" : "rgba(16,24,40,.08)";
  const INK = dark ? "#ffffff" : "#111827";
  const BODY = dark ? "#aab3c5" : "#4b5563";
  const MUTE = dark ? "#7c869c" : "#9ca3af";
  const iconAccent = dark ? shade(primary, 0.32) : shade(primary, -0.12);
  // Soft light gradient from the accent — used for the aura + profile band (both themes).
  const auraColor = rgba(primary, dark ? 0.24 : 0.13);
  const bandGrad = `linear-gradient(135deg, ${rgba(primary, dark ? 0.34 : 0.22)}, ${rgba(shade(primary, 0.45), 0.14)})`;
  const logoSrc = community.logo && String(community.logo).trim() ? community.logo : `${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`;
  const logoFallback = (e) => { e.currentTarget.onerror = null; e.currentTarget.src = `${process.env.PUBLIC_URL || ""}/images/flexicommunity-logo.png`; };

  return (
    <div className="relative min-h-screen" style={{ background: PAGE }}>
      {/* soft accent aura */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-72" style={{ background: `radial-gradient(60% 100% at 50% 0%, ${auraColor}, transparent 70%)` }} />

      {/* header */}
      <header className="sticky top-0 z-20 border-b backdrop-blur-xl" style={{ borderColor: BD, background: dark ? "rgba(14,18,32,.78)" : "rgba(255,255,255,.85)" }}>
        <div className="mx-auto flex h-14 max-w-5xl items-center justify-between px-4 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            {dark ? (
              <span className="inline-flex items-center rounded-lg bg-white px-2.5 py-1.5">
                <img src={logoSrc} alt={community.name || "FlexiCommunity"} className="h-6 w-auto max-w-[140px] object-contain" onError={logoFallback} />
              </span>
            ) : (
              <img src={logoSrc} alt={community.name || "FlexiCommunity"} className="h-8 w-auto max-w-[150px] object-contain" onError={logoFallback} />
            )}
            {member && myGroups.length > 0 && (
              <GroupSwitcher groups={myGroups} currentToken={undefined} primary={primary} onSelect={(gt) => onSwitchGroup?.(gt)} onDiscover={() => onDiscoverGroups?.()} />
            )}
          </div>
          <div className="flex items-center gap-2">
            {onBack && (
              <button type="button" onClick={onBack} className="group inline-flex items-center gap-2 rounded-lg border py-2 pl-3 pr-3.5 text-sm font-semibold shadow-sm transition active:scale-[0.98] sm:pr-4" style={{ borderColor: BD, background: dark ? "rgba(255,255,255,.05)" : "#fff", color: dark ? "#e5e7eb" : "#374151" }}>
                <svg className="h-4 w-4 transition group-hover:-translate-x-0.5" style={{ color: MUTE }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                <span className="hidden sm:inline">Back</span>
              </button>
            )}
            {!member && (
              <button onClick={onLogin} style={{ backgroundColor: primary }} className="rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:opacity-95">Log In</button>
            )}
          </div>
        </div>
      </header>

      <div className="relative mx-auto max-w-5xl px-4 py-6 sm:px-5 sm:py-10">
        <div className="grid gap-5 lg:grid-cols-[1fr_340px] lg:gap-6">
          {/* ---- profile card (top on mobile, right on desktop) ---- */}
          <aside className="order-first lg:order-last">
            <div className="overflow-hidden rounded-2xl border shadow-[0_24px_60px_-34px_rgba(0,0,0,.4)]" style={{ background: CARD, borderColor: BD }}>
              {/* accent band */}
              <div className="relative h-24" style={{ background: bandGrad }}>
                <div className="pointer-events-none absolute inset-0 opacity-70" style={{ background: `radial-gradient(80% 120% at 20% 0%, ${rgba(primary, 0.18)}, transparent 65%)` }} />
              </div>
              <div className="px-6 pb-6">
                {/* avatar overlapping the band */}
                <div className="relative z-10 -mt-14 flex justify-center">
                  <div className="rounded-full p-1.5 shadow-[0_12px_30px_-10px_rgba(0,0,0,.5)]" style={{ background: CARD }}>
                    {instructor.profile_image ? (
                      <img src={instructor.profile_image} alt={instructor.name} className="h-28 w-28 rounded-full object-cover" />
                    ) : (
                      <div className="flex h-28 w-28 items-center justify-center rounded-full text-4xl font-extrabold text-white" style={{ backgroundColor: primary }}>{initial}</div>
                    )}
                  </div>
                </div>
                <h1 className="mt-4 text-center text-xl font-extrabold" style={{ color: INK }}>{instructor.name}</h1>
                {instructor.short_description && (
                  <p className="mt-3 whitespace-pre-line text-center text-sm leading-relaxed" style={{ color: BODY }}>{instructor.short_description}</p>
                )}

                {(instructor.joined || instructor.location) && (
                  <div className="mt-5 space-y-2.5 border-t pt-5" style={{ borderColor: BD }}>
                    {instructor.joined && (
                      <div className="flex items-center gap-2.5 text-sm" style={{ color: BODY }}>
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: rgba(primary, dark ? 0.18 : 0.1), color: iconAccent }}>
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path strokeLinecap="round" d="M16 2v4M8 2v4M3 10h18" /></svg>
                        </span>
                        Joined {instructor.joined}
                      </div>
                    )}
                    {instructor.location && (
                      <div className="flex items-center gap-2.5 text-sm" style={{ color: BODY }}>
                        <span className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background: rgba(primary, dark ? 0.18 : 0.1), color: iconAccent }}>
                          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 21s-7-5.2-7-11a7 7 0 1114 0c0 5.8-7 11-7 11z" /><circle cx="12" cy="10" r="2.5" /></svg>
                        </span>
                        {instructor.location}
                      </div>
                    )}
                  </div>
                )}

                {instructor.social_links && Object.keys(instructor.social_links).length > 0 && (
                  <div className="mt-5 flex flex-wrap items-center justify-center gap-2 border-t pt-5" style={{ borderColor: BD }}>
                    {SOCIAL_ORDER.filter((k) => instructor.social_links[k]).map((k) => (
                      <a key={k} href={instructor.social_links[k]} target="_blank" rel="noreferrer" title={k}
                        className="flex h-10 w-10 items-center justify-center rounded-full border transition hover:-translate-y-0.5 hover:text-white"
                        style={{ borderColor: BD, color: BODY }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = primary; e.currentTarget.style.borderColor = primary; e.currentTarget.style.color = "#fff"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ""; e.currentTarget.style.borderColor = BD; e.currentTarget.style.color = BODY; }}>
                        {SOCIAL_ICONS[k]}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {community?.show_branding && <PoweredByFlexiFunnels className={`mt-4 ${dark ? "!text-gray-400 hover:!text-gray-200" : ""}`} />}
          </aside>

          {/* ---- owned groups ---- */}
          <div className="order-last min-w-0 lg:order-first">
            <div className="mb-4 flex items-center gap-2">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: primary }} />
              <h2 className="text-lg font-extrabold sm:text-xl" style={{ color: INK }}>Owned by {firstName}</h2>
            </div>
            {groups.length === 0 ? (
              <div className="rounded-2xl border py-12 text-center text-sm" style={{ background: CARD, borderColor: BD, color: MUTE }}>No groups linked to this group owner yet.</div>
            ) : (
              <div className="space-y-3">
                {groups.map((g) => (
                  <div key={g.group_id} className="group rounded-2xl border p-4 transition-all duration-300 hover:-translate-y-0.5 sm:p-5" style={{ background: CARD, borderColor: BD }}>
                    <div className="flex items-start gap-3 sm:items-center sm:gap-4">
                      <img src={g.banner} alt={g.name} className="h-14 w-14 shrink-0 rounded-xl object-cover" style={{ border: `1px solid ${BD}` }} />
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-bold" style={{ color: INK }}>{g.name}</div>
                        <div className="mt-0.5 text-[13px]" style={{ color: MUTE }}>{fmtCount(g.member_count)} members · {accessTypeLabel(g.access_type)}</div>
                      </div>
                      <button onClick={() => onView(g)} style={{ backgroundColor: primary, boxShadow: `0 8px 20px -8px ${rgba(primary, 0.8)}` }} className="shrink-0 rounded-xl px-5 py-2 text-sm font-bold text-white transition hover:-translate-y-0.5 sm:px-6">VIEW</button>
                    </div>
                    {g.description && <p className="mt-3 line-clamp-2 text-sm leading-relaxed" style={{ color: BODY }}>{g.description}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
